<script setup lang="ts">
import type { DataLayerItem } from '~layers/composables/useDataLayer'

const {
  search,
  searchDialogOpen,
  suggestions,
  recentSearches,
  loading,
  hasResults,
  categories,
  pages,
  products,
  searchResults,

  closeDialog,
  setFocusOnFirstItem,
  setFocusOnLastItem,
} = useQuickSearch()

const router = useRouter()
const { viewItemList } = useDataLayer()
const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({ item_list_name: 'quick-search', item_list_id: search.value }))

router.beforeEach(() => {
  closeDialog()
})

watch(loading, () => {
  if (!loading.value) {
    // searchDialogOpen.value = true
    if (products.value.length > 0) {
      viewItemList(products.value, dataLayerOverrides.value)
    }
  }
})
</script>

<template>
  <div
    class="backdrop search__backdrop"
    :class="{ 'backdrop--active': searchDialogOpen }"
    @click="closeDialog()"
  />

  <span
    v-if="searchDialogOpen"
    tabindex="0"
    class="search__focus-trap"
    aria-hidden="true"
    @focus="setFocusOnLastItem"
  />

  <div
    class="search__panel"
    role="dialog"
    aria-labelledby="search__form"
  >
    <div
      :ref="(el) => searchResults = el"
      class="search__results"
    >
      <div
        v-if="search.length > 3 && !hasResults && !loading"
        role="status"
        aria-live="polite"
        class="search__no-results"
      >
        <p>
          {{ $t('Unfortunately we were not able to find any results for your search') }}
        </p>

        <SearchTerms
          :name="$t('Recent search terms')"
          :terms="recentSearches"
          class="search__group--recent-searches"
        />
      </div>
      <div
        v-else-if="hasResults"
        role="listbox"
      >
        <div
          role="status"
          aria-live="polite"
          class="search__status"
        >
          {{ $t('We found {total} results for your search', { total: categories.length + pages.length + products.length }) }}
          {{ $t('Use arrow keys to navigate results.') }}
        </div>

        <SearchTerms
          v-if="!suggestions.length && !categories.length && !pages.length && recentSearches.length > 0"
          :name="$t('Recent search terms')"
          :terms="recentSearches"
          class="search__group--recent-searches"
        />

        <SearchTerms
          v-if="suggestions.length > 0"
          :name="$t('Suggestions')"
          :terms="suggestions"
          class="search__group--suggestions"
        />

        <SearchResultRoutes
          v-if="categories.length > 0"
          :routes="categories"
          :name="$t('Categories')"
          class="search__group--categories"
        />

        <SearchResultProducts v-if="products?.length > 0" />

        <SearchResultRoutes
          v-if="pages.length > 0"
          :routes="pages"
          :name="$t('Pages')"
          class="search__group--pages"
        />
      </div>

      <div
        v-else
        role="listbox"
      >
        <SearchTerms
          v-if="recentSearches.length > 0"
          :name="$t('Recent search terms')"
          :terms="recentSearches"
          class="search__group--recent-searches"
        />

        <SearchTerms
          v-if="suggestions.length > 0"
          :name="$t('Suggestions')"
          :terms="suggestions"
          class="search__group--suggestions"
        />
      </div>
    </div>

    <!-- <pre>suggestions: {{ suggestions }}</pre> -->
    <!-- <pre>recentSearches: {{ recentSearches }}</pre> -->
    <!-- <pre>categories: {{ categories }}</pre> -->
    <!-- <pre>pages: {{ pages }}</pre> -->
  </div>

  <span
    v-if="searchDialogOpen"
    tabindex="0"
    class="search__focus-trap"
    aria-hidden="true"
    @focus="setFocusOnFirstItem"
  />
</template>

<style>
@import url("~layers/components/Search/Panel.css");
</style>
